import type { ReactNode } from 'react';

/** Pastel category set (owner decision 3 Oct 2026): lavender, coral, sun, sky, mint. Colour is never the only signal: pair with a label. */
export const TONES = ['lavender', 'coral', 'sun', 'sky', 'mint'] as const;
export type Tone = (typeof TONES)[number];
/** Stable tone for the n-th item of a list. */
export const toneAt = (i: number): Tone => TONES[((i % TONES.length) + TONES.length) % TONES.length]!;

/** Round icon tile in a pastel tone; decorative (aria-hidden), the adjacent text carries the meaning. */
export function ToneTile({ tone, size = 'md', children }: { tone: Tone; size?: 'sm' | 'md'; children: ReactNode }) {
  return <span className="qm-tile" data-tone={tone} data-size={size} aria-hidden="true">{children}</span>;
}

/** Small pastel chip with black text. */
export function ToneChip({ tone, children }: { tone: Tone; children: ReactNode }) {
  return <span className="qm-chip" data-tone={tone}>{children}</span>;
}

/** Horizontal score bar, 0-100, in a pastel tone. The value is exposed as a meter. */
export function ScoreBar({ value, tone, label }: { value: number; tone: Tone; label: string }) {
  const v = Math.max(0, Math.min(100, Math.round(value)));
  return (
    <span className="qm-bar" data-tone={tone} role="meter" aria-label={label} aria-valuemin={0} aria-valuemax={100} aria-valuenow={v}>
      <span style={{ inlineSize: `${v}%` }} />
    </span>
  );
}

/** Growth-score gauge (0-100): coral > sun > mint arc over a soft track. */
export function Gauge({ value, label }: { value: number; label: string }) {
  const v = Math.max(0, Math.min(100, value));
  const c = Math.PI * 70;
  return (
    <svg className="qm-gauge" viewBox="0 0 180 104" role="img" aria-label={label}>
      <defs>
        <linearGradient id="qm-gauge-grad" x1="0" x2="1">
          <stop offset="0" stopColor="var(--q-coral)" />
          <stop offset=".5" stopColor="var(--q-sun)" />
          <stop offset="1" stopColor="var(--q-mint)" />
        </linearGradient>
      </defs>
      <path d="M20 94 A70 70 0 0 1 160 94" fill="none" stroke="var(--q-lavender-soft)" strokeWidth="14" strokeLinecap="round" />
      <path className="qm-gauge__arc" d="M20 94 A70 70 0 0 1 160 94" fill="none" stroke="url(#qm-gauge-grad)" strokeWidth="14" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - v / 100)} />
      <text x="90" y="86" textAnchor="middle">{Math.round(v)}</text>
    </svg>
  );
}
